import React from "react";
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export const ACCEPTED_TYPES = ".xlsx,.xls";

interface FileDropzoneProps {
  fileInputRef: React.RefObject<HTMLInputElement>;
  selectedFile: File | null;
  uploading: boolean;
  parsing: boolean;
  isDragging: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onReset: () => void;
}

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  fileInputRef,
  selectedFile,
  uploading,
  parsing,
  isDragging,
  onFileChange,
  onDrop,
  onDragOver,
  onDragLeave,
  onReset,
}) => {
  const isBusy = uploading || parsing;

  return (
    <>
      <Box
        onClick={() => !isBusy && fileInputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        sx={{
          p: 4,
          border: "2px dashed",
          borderColor: isDragging ? "primary.main" : "#ccc",
          borderRadius: 2,
          backgroundColor: isDragging ? "rgba(25, 118, 210, 0.06)" : "#fafafa",
          textAlign: "center",
          cursor: isBusy ? "default" : "pointer",
          transition: "border-color .2s, background-color .2s",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          onChange={onFileChange}
          onClick={(e) => {
            (e.target as HTMLInputElement).value = "";
            e.stopPropagation();
          }}
          style={{ display: "none" }}
          id="audit-data-file-input"
        />

        <CloudUploadOutlinedIcon sx={{ fontSize: 46, color: "text.secondary", mb: 1 }} />
        <Typography variant="body1" fontWeight={600} gutterBottom>
          Drag &amp; drop your Audit Data Excel file here
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            flexWrap: "wrap",
            my: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            or
          </Typography>
          <Button
            disabled={isBusy}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            sx={{
              backgroundColor: "#eed076",
              color: "#222222",
              fontWeight: 600,
              borderRadius: "20px",
              textTransform: "none",
              px: 2.5,
              py: 0.5,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#e3c265",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#999999",
              },
            }}
          >
            Browse Files
          </Button>
          <Tooltip
            arrow
            placement="right"
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" display="block" fontWeight={600} mb={0.5}>
                  File name format
                </Typography>
                <Typography variant="caption" display="block">
                  Format: <strong>AD_&lt;CB_Code&gt;_&lt;TE_ID&gt;_&lt;Audit Dates&gt;_&lt;Submission Date&gt;.xlsx</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
                  Matching is case-insensitive.
                </Typography>
              </Box>
            }
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ color: "text.secondary", cursor: "help" }}
              aria-label="Audit Data file name format help"
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Accepts .xlsx / .xls files
        </Typography>

        {selectedFile && (
          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "center" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Chip
              icon={<InsertDriveFileOutlinedIcon />}
              label={`${selectedFile.name} (${(selectedFile.size / 1024).toFixed(1)} KB)`}
              onDelete={isBusy ? undefined : onReset}
              variant="outlined"
              sx={{
                maxWidth: "100%",
                height: "auto",
                py: 0.5,
                "& .MuiChip-label": { whiteSpace: "normal", wordBreak: "break-all" },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Footer info text matching design */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 2.5 }}>
        <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary">
          File naming format for the template to be uploaded:{" "}
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            AD_&lt;CB_Code&gt;_&lt;TE_ID&gt;_&lt;Audit Dates&gt;_&lt;Submission Date&gt;.xlsx
          </Box>
        </Typography>
      </Box>
    </>
  );
};
